import { useEffect, useState } from 'react'
import ModalComponent from '..'
import { formatCurrency } from '../../../helpers'
import { useFetchAllListArticlesQuery } from '../../../redux-toolkits/lists/list.slice'
import { QueryProps } from '../../../redux-toolkits/shared/shared.type'
import { Article } from '../../../redux-toolkits/lists/list.types'
import { Button } from '../../button'
import { Checkbox } from '../../inputs/Checkbox'
import { SearchInput } from '../../inputs/searchInput'
import { TableComponent } from '../../table'

type V = Article

export const SelectGoodList = ({
  maxSelection,
  open,
  goodLists,
  setGoodLists,
  section,
  fulfilmentCentreId,
}: {
  maxSelection?: number
  open?: boolean
  goodLists: V[]
  setGoodLists: (goodLists: V[]) => void
  section?: 'LIFESTYLE' | 'FOOD' | 'EVENT'
  fulfilmentCentreId?: string
}) => {
  const [queryParam, setQueryParam] = useState<QueryProps>({
    page: 1,
    limit: 20,
    search: undefined,
  })

  const { data, isFetching } = useFetchAllListArticlesQuery(
    {
      query: {
        ...queryParam,
        status: 'PUBLISHED',
        type: section,
        fulfilmentCentre: fulfilmentCentreId,
      },
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !open,
    },
  )

  return (
    <div
      className="
        flex flex-col gap-2
        cursor-pointer
        "
    >
      <div className="bg-white pb-2 !max-w-full sticky top-0 z-10">
        <SearchInput
          searchFunction={(search) => {
            setQueryParam({
              ...queryParam,
              search: search === '' ? undefined : search,
              skip: 0,
            })
          }}
          placeholder="Search Good Lists"
          className="!max-w-full"
        />
      </div>

      <div className="max-w-full flex-1 overflow-y-scroll">
        <TableComponent
          fixedTableWidth
          rows={(data?.data?.result ?? []).map((item) => {
            return {
              id: item._id,
              content: [
                <Checkbox
                  className="!gap-6 !w-full"
                  disabled={
                    maxSelection !== undefined &&
                    goodLists.length >= maxSelection &&
                    goodLists.length != 1 &&
                    !goodLists.map((c) => c._id).includes(item._id)
                  }
                  checkStatus={
                    goodLists.map((c) => c._id)?.includes(item._id) ?? false
                  }
                  handleChange={(checked) => {
                    if (checked) {
                      const dataCopy: V[] = JSON.parse(
                        JSON.stringify(goodLists),
                      )
                      if (
                        maxSelection !== undefined &&
                        dataCopy.length >= maxSelection
                      ) {
                        dataCopy.shift()
                      }
                      dataCopy.push(item)

                      setGoodLists(dataCopy)
                    } else {
                      const dataCopy: V[] = JSON.parse(
                        JSON.stringify(goodLists),
                      )
                      setGoodLists(
                        dataCopy.filter(
                          (singleData) => item._id !== singleData._id,
                        ),
                      )
                    }
                  }}
                  key={item._id}
                  text={
                    <div className="flex !w-full gap-3 items-center max-w-full">
                      <img
                        src={item.bannerUrl}
                        className="block w-[140px] h-[140px] object-cover rounded-md"
                        alt={`icon`}
                      />

                      <div className="max-w-full flex flex-col gap-1">
                        <p className="flex-1 text-base text-black font-normal">
                          {item.title}
                        </p>

                        <p className="text-sm text-sec-black line-clamp-3 break-words">
                          {item.description}
                        </p>

                        <p className="text-sm text-black">
                          Starting from
                          <br />
                          {formatCurrency(item.startingFrom)}
                        </p>
                      </div>
                    </div>
                  }
                />,
              ],
            }
          })}
          loading={isFetching}
          isEmpty={[0, undefined].includes(data?.data?.result?.length)}
        />
      </div>
    </div>
  )
}

export const SelectGoodListModal = ({
  open,
  setOpen,
  onCloseCallback,
  onSuccessCallback,
  selectedGoodLists,
  maxSelection,
  loading = false,
  section,
  fulfilmentCentreId,
}: {
  open: boolean
  setOpen: (val: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: V[]) => void
  selectedGoodLists?: V[]
  maxSelection?: number
  loading?: boolean
  section?: 'LIFESTYLE' | 'FOOD' | 'EVENT'
  fulfilmentCentreId?: string
}) => {
  const [goodLists, setGoodLists] = useState<V[]>([])

  useEffect(() => {
    if (open) {
      setGoodLists(selectedGoodLists ?? [])
    }
  }, [open])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        onCloseCallback && onCloseCallback()
      }}
      title="Select Good Lists"
      childrenClassName="md:!max-w-[600px]"
    >
      <div className="flex flex-col gap-6">
        <SelectGoodList
          maxSelection={maxSelection}
          open={open}
          goodLists={goodLists}
          setGoodLists={setGoodLists}
          section={section}
          fulfilmentCentreId={fulfilmentCentreId}
        />

        <Button
          type="button"
          primary
          label="Continue"
          onClick={() => {
            onSuccessCallback && onSuccessCallback(goodLists)
          }}
          loading={loading}
          disabled={loading || goodLists.length < 1}
          className="sticky bottom-0"
        />
      </div>
    </ModalComponent>
  )
}
